import React from "react";
import { motion } from "framer-motion";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.05 },
  },
};

const riseUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 180, damping: 22 },
  },
};

export default function LeadFormHero() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: { xs: 7, md: 10 },
        pb: { xs: 12, md: 18 },
        background:
          "linear-gradient(155deg, #0f172a 0%, #1e293b 35%, #1e3a5f 70%, #1e3a8a 100%)",
      }}
    >
      {/* Grid pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.03,
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          zIndex: 0,
        }}
      />

      {/* Glow orbs */}
      <Box
        component={motion.div}
        animate={{ x: [0, 30, 0], y: [0, -15, 0] }}
        transition={{ duration: 14, repeat: Infinity, repeatType: "reverse" }}
        sx={{
          position: "absolute",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 65%)",
          top: -120,
          right: -80,
          zIndex: 0,
        }}
      />
      <Box
        component={motion.div}
        animate={{ x: [0, -20, 0], y: [0, 20, 0] }}
        transition={{ duration: 18, repeat: Infinity, repeatType: "reverse" }}
        sx={{
          position: "absolute",
          width: 350,
          height: 350,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 65%)",
          bottom: -100,
          left: -50,
          zIndex: 0,
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div variants={stagger} initial="hidden" animate="visible">
          {/* Badge */}
          <motion.div variants={riseUp}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
              sx={{ mb: 3 }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 1,
                  backgroundColor: alpha("#2563eb", 0.5),
                }}
              />
              <Typography
                variant="overline"
                sx={{
                  color: "#60a5fa",
                  fontWeight: 700,
                  letterSpacing: "3px",
                  fontSize: "0.7rem",
                }}
              >
                TENHO INTERESSE
              </Typography>
              <Box
                sx={{
                  width: 32,
                  height: 1,
                  backgroundColor: alpha("#2563eb", 0.5),
                }}
              />
            </Stack>
          </motion.div>

          {/* Heading */}
          <motion.div variants={riseUp}>
            <Typography
              variant={isMobile ? "h4" : "h2"}
              fontWeight={800}
              align="center"
              sx={{
                color: "#fff",
                mb: 2.5,
                letterSpacing: "-0.025em",
                lineHeight: 1.15,
              }}
            >
              Traga seu negocio para a{" "}
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(90deg, #60a5fa, #2563eb, #10b981)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Slapie
              </Box>
            </Typography>
          </motion.div>

          {/* Subtitle */}
          <motion.div variants={riseUp}>
            <Typography
              variant="h6"
              align="center"
              fontWeight={400}
              sx={{
                color: alpha("#fff", 0.6),
                maxWidth: 520,
                mx: "auto",
                lineHeight: 1.7,
                fontSize: { xs: "0.95rem", md: "1.1rem" },
              }}
            >
              Deixe seu contato e nossa equipe apresentará a plataforma
              ideal para a sua operação.
            </Typography>
          </motion.div>
        </motion.div>
      </Container>

      {/* Angled separator */}
      <Box
        sx={{
          position: "absolute",
          bottom: -1,
          left: 0,
          right: 0,
          lineHeight: 0,
        }}
      >
        <svg
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
          style={{ width: "100%", height: 50, display: "block" }}
        >
          <path
            d="M0 80L1440 0V80H0Z"
            fill={theme.palette.background.default}
          />
        </svg>
      </Box>
    </Box>
  );
}
